/*
 * PDP 2026 – Molekule app tabs (<pdp-app-tabs>, used by sections/pdp-app-tabs.liquid).
 *
 * Purpose: a vertical tab list switches the feature panel (icon, title, text), the image and the dots.
 * On mobile the panels are a horizontal CSS scroll-snap slider (see assets/pdp-app-tabs.css): swiping it
 * selects the matching tab, and selecting a tab scrolls the slider. On desktop the slider is not scrollable,
 * so the same handlers simply swap the visible panel.
 *
 * Config (read from the markup, no options):
 *   [role="tab"][data-index]  tab buttons (also carry the theme editor block attributes);
 *                             the one rendered with aria-selected="true" is selected on load
 *   [data-app-scroller]       panel container – scrollable on mobile only
 *   [data-app-panel]          panels, one per tab, in the same order
 *   [data-app-media]          figures, one per tab; inactive ones get the `hidden` attribute
 *   [data-app-dot]            pagination dots (decorative)
 *
 * Usage:
 *   <script src="{{ 'pdp-app-tabs.js' | asset_url }}" defer></script>
 *   <pdp-app-tabs> …tablist, media, panels, dots… </pdp-app-tabs>
 * Works with several instances per page and re-initialises when the theme editor reloads the section.
 */

if (!customElements.get('pdp-app-tabs')) {
  /** Delay (ms) after the last scroll event before the slider position is synced to the tabs. */
  const SCROLL_SETTLE_DELAY = 100;

  class PdpAppTabs extends HTMLElement {
    /**
     * Collects the parts and binds the listeners.
     * @returns {void}
     */
    connectedCallback() {
      this.tabList = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.scroller = this.querySelector('[data-app-scroller]');
      this.panels = Array.from(this.querySelectorAll('[data-app-panel]'));
      this.media = Array.from(this.querySelectorAll('[data-app-media]'));
      this.dots = Array.from(this.querySelectorAll('[data-app-dot]'));

      if (!this.tabList || this.tabs.length === 0) {
        return;
      }

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

      this.index = Math.max(0, selectedIndex);
      this.scrollTimer = null;

      this.onTabClick = this.onTabClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.tabList.addEventListener('click', this.onTabClick);
      this.tabList.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.scroller) {
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      }

      if (this.index > 0) {
        requestAnimationFrame(() => this.scrollToPanel(this.index, 'instant'));
      }
    }

    /**
     * Removes the listeners (section removed or re-rendered in the theme editor).
     * @returns {void}
     */
    disconnectedCallback() {
      if (!this.tabList) {
        return;
      }

      this.tabList.removeEventListener('click', this.onTabClick);
      this.tabList.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);

      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this.onScroll);
      }

      clearTimeout(this.scrollTimer);
    }

    /**
     * Activates a tab and everything that belongs to it.
     * @param {number} index - Index of the tab to activate.
     * @param {{ focus?: boolean, scroll?: boolean }} [options] - Move focus to the tab / scroll the slider to its panel.
     * @returns {void}
     */
    select(index, options = {}) {
      if (index < 0 || index >= this.tabs.length) {
        return;
      }

      this.index = index;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;

        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });

      this.media.forEach((figure, i) => {
        figure.hidden = i !== index;
      });

      if (options.focus) {
        this.tabs[index].focus();
      }

      if (options.scroll) {
        this.scrollToPanel(index);
      }
    }

    /**
     * Whether the panels currently form a scrollable slider (mobile layout).
     * @returns {boolean}
     */
    isSlider() {
      if (!this.scroller) {
        return false;
      }

      return this.scroller.scrollWidth > this.scroller.clientWidth + 1;
    }

    /**
     * Scrolls the slider so the given panel snaps to the start (no-op on desktop).
     * By default smooth scrolling and its reduced-motion fallback come from CSS `scroll-behavior`.
     * @param {number} index - Panel index.
     * @param {ScrollBehavior} [behavior] - Override, e.g. 'instant' for the initially selected tab.
     * @returns {void}
     */
    scrollToPanel(index, behavior) {
      const panel = this.panels[index];

      if (!panel || !this.isSlider()) {
        return;
      }

      const options = { left: panel.offsetLeft };

      if (behavior) {
        options.behavior = behavior;
      }

      this.scroller.scrollTo(options);
    }

    /**
     * Returns the index of the panel closest to the slider's current scroll position.
     * @returns {number}
     */
    getPanelIndexFromScroll() {
      const scrollLeft = this.scroller.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.panels.forEach((panel, i) => {
        const distance = Math.abs(panel.offsetLeft - scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });

      return closestIndex;
    }

    /**
     * Click on a tab button (delegated from the tab list).
     * @param {MouseEvent} event - Click event.
     * @returns {void}
     */
    onTabClick(event) {
      const tab = event.target.closest('[role="tab"]');

      if (!tab) {
        return;
      }

      this.select(this.tabs.indexOf(tab), { scroll: true });
    }

    /**
     * Arrow / Home / End keyboard navigation between tabs (WAI-ARIA tabs pattern, automatic activation).
     * @param {KeyboardEvent} event - Keydown event.
     * @returns {void}
     */
    onKeydown(event) {
      const lastIndex = this.tabs.length - 1;
      let nextIndex = null;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = this.index === lastIndex ? 0 : this.index + 1;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = this.index === 0 ? lastIndex : this.index - 1;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = lastIndex;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();

      this.select(nextIndex, { focus: true, scroll: true });
    }

    /**
     * Slider scroll: waits until scrolling settles, then selects the panel that snapped into place.
     * @returns {void}
     */
    onScroll() {
      clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        if (!this.isSlider()) {
          return;
        }

        const index = this.getPanelIndexFromScroll();

        if (index !== this.index) {
          this.select(index);
        }
      }, SCROLL_SETTLE_DELAY);
    }

    /**
     * Theme editor: show the tab whose block was selected in the sidebar.
     * @param {CustomEvent} event - shopify:block:select event.
     * @returns {void}
     */
    onBlockSelect(event) {
      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;

      if (!tab || !this.contains(tab)) {
        return;
      }

      this.select(this.tabs.indexOf(tab), { scroll: true });
    }
  }

  customElements.define('pdp-app-tabs', PdpAppTabs);
}
